/**
 * Utility functions for safely handling Firebase Timestamps and date conversions
 */

/**
 * Safely converts various timestamp formats to a JavaScript Date object
 * @param timestamp - The timestamp to convert (Firestore Timestamp, Date, string, or number)
 * @returns A valid Date object, or current date if conversion fails
 */
export function safeTimestampToDate(timestamp: any): Date {
  if (!timestamp) return new Date();
  
  // If it's already a Date object
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a Firestore Timestamp with toDate method
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a Firestore Timestamp object with seconds property
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // If it's a string or number, try to parse it
  const parsed = new Date(timestamp);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

/**
 * Safely converts a timestamp to a Date object, returns undefined if invalid
 * @param timestamp - The timestamp to convert
 * @returns A valid Date object, or undefined if conversion fails
 */
export function safeTimestampToDateOrUndefined(timestamp: any): Date | undefined {
  if (!timestamp) return undefined;
  
  // If it's already a Date object
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a Firestore Timestamp with toDate method
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a Firestore Timestamp object with seconds property
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // If it's a string, try to parse it
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  
  return undefined;
}

/**
 * Checks if a timestamp is a valid Firestore Timestamp
 * @param timestamp - The timestamp to check
 * @returns true if it's a valid Firestore Timestamp
 */
export function isFirestoreTimestamp(timestamp: any): boolean {
  return timestamp && typeof timestamp.toDate === 'function';
}

/**
 * Formats a timestamp for display
 * @param timestamp - The timestamp to format
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 */
export function formatTimestamp(
  timestamp: any, 
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }
): string {
  const date = safeTimestampToDate(timestamp);
  return date.toLocaleDateString('en-US', options);
} 